import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import {
    Box, Divider, Typography, Container, Paper, Grid, FormControl, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';

import {
    DataGrid,
} from '@mui/x-data-grid';

import CustomMessage from '../../utils/components/CustomMessage';
import CustomChip from '../../utils/components/CustomChip';
import { formatMoney } from '../../utils/functions/Utils';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';

const EditarPrecios = (props) => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [showConfirmPriceScale, setShowConfirmPriceScale] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [scale, setScale] = useState(0);

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' });
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Editar Precios';
    }, [auth])

    useEffect(() => {
        getData();
    }, [])

    const getData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/configs/company/${auth.user.company}`);
            if (res.status === 200) {
                setData(res.data);
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            setLoading(false);
        }
    }

    const getProductsScale = async () => {
        setLoading(true);
        try {
            const res = await axios.post('/api/products/filter', { data: '...' });
            let tmpPrecio = 0;
            if (res.status === 200) {
                if (res.data.length > 0) {
                    let products = res.data.map(({ product }) => product)
                    products = products.map(product => {
                        tmpPrecio = Number(product.precioUnitario) * (1 + Number(scale / 100))
                        return {
                            ...product,
                            precioUnitarioAnterior: `$ ${formatMoney(product.precioUnitario)}`,
                            precioUnitario: `$ ${formatMoney(tmpPrecio)}`
                        }
                    });
                    setSearchResults(products);
                    setLoading(false);
                }
            }
        } catch (error) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al obtener los productos, intentelo mas tarde', severity: 'error' } });
            console.log(error);
            setLoading(false);
        }
    }

    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    const handleScale = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post('/api/products/scalePrice', { scale: Number(scale) });
            if (res.status === 200) {
                setApiRes(state => { return { ...state, show: true, message: 'Precios actualizados', severity: 'success' } });
                setShowConfirmPriceScale(false);
                getData();
                getProductsScale();
                setLoading(false);
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al actualizar los precios, favor de intentarlo mas tarde', severity: 'error' } });
            console.log(err);
            setLoading(false);
        }
    }

    const handleCloseView = () => {
        setShowConfirmPriceScale(false);
    }

    const renderProducts = () => {
        if (searchResults.length === 0) return;
        return (
            <>
                <Divider className='my-3' textAlign="center">
                    <CustomChip variant='outlined' estatus='AUTORIZADO' label='Simulación de precios' className='text-bold' />
                </Divider>

                <DataGrid
                    rows={searchResults}
                    density='comfortable'
                    getRowId={(row) => row._id}
                    sx={{ height: `${props.height ? props.height : height - 100}px`, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                    columns={[
                        { field: 'clave', headerName: 'Clave', maxWidth: 300, minWidth: 200, flex: 1, cellClassName: 'text-bold' },
                        { field: 'precioUnitarioAnterior', headerName: 'Precio actual', maxWidth: 300, minWidth: 200, flex: 1, cellClassName: 'text-bold h6' },
                        { field: 'precioUnitario', headerName: 'Nuevo precio', maxWidth: 300, minWidth: 200, flex: 1, cellClassName: 'text-success text-bold h6' },
                        { field: 'cantidad', headerName: 'Existencia', type: 'number', maxWidth: 80 },
                        { field: 'nombre', headerName: 'Producto', maxWidth: 200, minWidth: 100, flex: 1 },
                        { field: 'descripcion', headerName: 'Descripción', minWidth: 200, flex: 1 },
                        { field: 'marca', headerName: 'Marca' },
                        { field: 'modelo', headerName: 'Modelo', minWidth: 200, },
                        { field: 'categoria', headerName: 'Categoría', minWidth: 120, },
                        {
                            field: 'imagen',
                            headerName: 'Imagen',
                            headerAlign: 'center',
                            align: 'center',
                            sortable: false,
                            filterable: false,
                            disableColumnMenu: true,
                            renderCell: (row) => (
                                <>
                                    <LazyLoadImage src={row.row.imagen} alt={row.row.nombre} style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
                                </>
                            )
                        },
                    ]}
                />
            </>
        )
    }

    const renderPriceScale = () => {
        return (
            <Dialog
                open={showConfirmPriceScale}
                onClose={() => handleCloseView()}
                fullWidth={true}
                maxWidth={'xl'}
                scroll='paper'
            >
                <DialogTitle >Confirmar actualización de precios</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant='body1'>Se actualizará el <span className='text-bold'>precio al publico</span> de todos los productos del almacén, ¿Desea continuar?</Typography>
                    <Typography variant='body2'><span className='text-bold'>Esta acción no se puede deshacer</span></Typography>
                    <Button sx={{ my: 3 }} variant='contained' disabled={loading} color='success' onClick={(e) => handleScale(e)} >Actualizar &nbsp; <i className="fad fa-upload"></i></Button>
                </DialogContent>
                <DialogActions >
                    <Button color='warning' variant='contained' onClick={() => handleCloseView()}>Cerrar &nbsp; <i className="fad fa-times"></i></Button>
                </DialogActions>
            </Dialog>
        )
    }

    return (
        <Container maxWidth='xxl' style={{ marginTop: '10px' }}>
            <Paper sx={{ padding: '24px 0px' }} elevation={3}>

                <Container maxWidth='xxl'>
                    <Box className='text-center' >
                        <Typography variant='h5' style={{ fontWeight: 700 }}>Editar Precios</Typography>
                    </Box>
                    <Container maxWidth='xl'>
                        <Button size='small' variant='outlined' onClick={() => navigate('/config')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                    </Container>

                    <Divider className='my-3' textAlign="center">
                        <CustomChip variant='outlined' estatus='DIAGNOSTICADO' label='Escalar Precios' className='text-bold' />
                    </Divider>

                    <Divider className='my-3' textAlign="left">
                        <CustomChip variant='outlined' estatus='AUTORIZADO' label='Escalar precios con porcentaje' className='text-bold' />
                    </Divider>

                    <Grid container spacing={3}>
                        <Grid item xs={6} md={3}>
                            <FormControl sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                <TextField
                                    label="% de aumento (1% - 100%)"
                                    variant="outlined"
                                    error={isNaN(Number(scale)) ? true : false}
                                    helperText={isNaN(Number(scale)) ? 'El porcentaje debe ser un número entre 1 y 100' : null}
                                    value={scale}
                                    onChange={(e) => setScale(e.target.value)} />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={4} style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='h6' sx={{ color: '#999' }}>Ejemplo: </Typography>
                            <Typography variant='h6' sx={{ color: 'info.main', ml: 2 }}>200 MXN</Typography>
                            <Typography variant='h6' sx={{ color: '#999', ml: 2 }}>+</Typography>
                            <Typography variant='h6' sx={{ color: 'secondary.main', ml: 2 }}>{`${scale}`} %</Typography>
                            <Typography variant='h6' sx={{ color: '#999', ml: 2 }}>=</Typography>
                            <Typography variant='h6' sx={{ color: 'success.main', ml: 2 }}>{`$ ${200 + 200 * Number(scale / 100)} MXN`}</Typography>
                        </Grid>
                        <Grid item xs={12} md={2} sx={{ my: 2, display: 'flex', justifyContent: 'end' }}>
                            <Button variant="contained" onClick={() => getProductsScale()} color="info" disabled={loading}>Simular &nbsp; <i className="fal fa-atom"></i></Button>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={12} sx={{ my: 2, display: 'flex', justifyContent: 'end' }}>
                            <Button variant="contained" onClick={() => setShowConfirmPriceScale(true)} color="success" disabled={loading}>Actualizar Precios&nbsp; <i className="fad fa-upload"></i></Button>
                        </Grid>
                    </Grid>

                    {renderProducts()}
                    {renderPriceScale()}

                </Container>
                <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
            </Paper>
        </Container>
    );
}
export default EditarPrecios